<script lang="ts" setup>
import type { FocusEvent } from '@/objects/generated/classes/FocusEvent';
import ListComp from '../List/ListComp.vue';
import { useGuiStore } from '@/stores';
import { ref, watch } from 'vue';
import type ListCompExpose from '@/objects/ListCompExpose';
import type { GridColumnOptions } from '../Grid/Classes';
import { GridColumnOptionsBuilder } from '../Grid/Classes/GridColumnOptions';
import FocusEventFromToHelper from './FocusEventFromToHelper.vue';
import { isToday } from '@/utilities/dateutils';
import type { IGridRowCssRule } from '../Grid/Classes/IGridRowCssRule';
import { DisplayTick } from '..';
import { DropdownItemMenu, DropdownItemButton } from '@/objects';

const props = defineProps<{
    events: FocusEvent[];
}>();

const listComp = ref<ListCompExpose<FocusEvent> | null>(null);

const data = ref<FocusEvent[]>(props.events);

const emit = defineEmits<{
    (e: 'hide', focusevent: FocusEvent): void;
}>();

const addCustomColumns = (columns: GridColumnOptions<FocusEvent>[]) => {
    new GridColumnOptionsBuilder<FocusEvent>('Setup.LiveEnabled', 'focus.liveenabled')
        .ReadOnly()
        .SortDisabled()
        .Size(100, 100)
        .CellRenderer(DisplayTick)
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<FocusEvent>('SignupSettings.OpenFrom', 'focus.signupopens')
        .ReadOnly()
        .SortDisabled()
        .Size(160, 160)
        .CellRenderer(FocusEventFromToHelper)
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<FocusEvent>('Organizer.Name', 'eventinfobase.organizerid').ReadOnly().SortDisabled().Insert(columns, 3);

    return columns;
};

/**
 * The row class rules for the grid
 */
const rowClassRules: IGridRowCssRule<FocusEvent>[] = [
    {
        className: 'ag-row-today',
        func: (data: FocusEvent) => {
            return data.SignupSettings == null || data.SignupSettings.OpenFrom == null ? false : isToday(data.SignupSettings.OpenFrom!);
        },
    },
];

watch(
    () => props.events,
    () => {
        data.value = props.events;
        listComp.value?.dataUpdated();
        setTimeout(() => {
            listComp.value?.resizeColumns();
        }, 200);
    },
    { immediate: true },
);

/**
 * A list of actions for the quick buttons
 */
const actionGroups = [
    DropdownItemMenu.create({
        icon: 'fa-gear',
        translationkey: 'general.actions',
        dropdownItems: [
            DropdownItemButton.create({
                translationkey: 'general.confirm',
                icon: 'fa-check',
                action: () => {
                    console.log('confirm and hide event');
                },
            }),
        ],
    }),
];
</script>

<template>
    <ListComp
        ref="listComp"
        v-model="data"
        :gui="useGuiStore().FocusEvent"
        :readonly="true"
        :select-all="true"
        :hide-default-buttons="true"
        :hide-search-field="true"
        :quick-buttons="actionGroups"
        :listMode="true"
        :customColumns="addCustomColumns"
        :row-class-rules="rowClassRules"
        resizeStrategy="sizeColumnsToFit"
    />
</template>
